import { useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { quizzesQueryKey } from '~teacher/hook/useQuizzes';

import { QuizItemCache } from '../../api/quiz/getQuizzes/normalize/toQuizItemCache';
import { uniqueIdFactory } from '../../api/quiz/getQuizzes/normalize/uniqueIds';
import { Folder, QuizOrFolder } from '../../api/quiz/getQuizzes/types';
import {
  MoveQuizFolderParam,
  moveQuizFolder,
} from '../../api/quiz/moveQuizFolder/moveQuizFolder';

const isNotOldItem = (oldItem: QuizOrFolder) => (oldItemId) =>
  oldItemId !== oldItem.uniqueId;

export const cacheUpdater = ({
  folderIds = [],
  quizIds = [],
  parentId,
}: {
  folderIds?: number[];
  quizIds?: number[];
  parentId: number;
}) =>
  produce((draft: QuizItemCache) => {
    const movedItems = [
      ...(folderIds ?? []).map(uniqueIdFactory.folder.toUniqueId),
      ...(quizIds ?? []).map(uniqueIdFactory.quiz.toUniqueId),
    ];

    movedItems.forEach((moveItemId) => {
      const moveItem = draft.itemById[moveItemId];

      // Update the old parent and remove references to each item.

      if (moveItem.parentId === null) {
        draft.rootItems = draft.rootItems.filter(isNotOldItem(moveItem));
      } else {
        const oldItemParent =
          draft.itemById[uniqueIdFactory.folder.toUniqueId(moveItem.parentId)];
        if (oldItemParent?.type === 'folder') {
          oldItemParent.children = oldItemParent.children.filter(
            isNotOldItem(moveItem)
          );
        }
      }

      // update the current item's parent reference
      moveItem.parentId = parentId;
      if (parentId === null) {
        draft.rootItems.push(moveItem.uniqueId);
      } else {
        const newItemParent = draft.itemById[
          uniqueIdFactory.folder.toUniqueId(parentId)
        ] as Folder;
        newItemParent.children.push(moveItemId);
      }
    });

    return draft;
  });

export function useMoveQuizFolder() {
  const queryClient = useQueryClient();
  return useMutation(['quizzes', 'move'], {
    mutationFn: moveQuizFolder,
    onMutate: async ({ folderIds, parentId, quizIds }: MoveQuizFolderParam) => {
      await queryClient.cancelQueries(quizzesQueryKey.quiz);
      const previousQuizItems = queryClient.getQueryData<QuizItemCache>(
        quizzesQueryKey.quiz
      );
      queryClient.setQueryData<QuizItemCache>(
        quizzesQueryKey.quiz,
        cacheUpdater({ folderIds, quizIds, parentId })
      );
      return { previousQuizItems };
    },
    onError: (
      error: unknown,
      variables: MoveQuizFolderParam,
      context: { previousQuizItems: QuizItemCache }
    ) => {
      queryClient.setQueryData(quizzesQueryKey.quiz, context.previousQuizItems);
    },
  });
}
